// ============================================================
// ANSICHTSSACHE-AGENT – Das Sichtfenster einer Liste
// ============================================================
// Wie viele Zeilen eine Liste WIRKLICH in den Browser schickt.
//
// WARUM ES DAS BRAUCHT: eine Liste, die ihren ganzen Bestand
// zeichnet, ist so lange unauffaellig, wie der Bestand klein
// ist. Am 21.09.2026 stand der Arbeitsvorrat im Banking bei
// 1.476 Umsaetzen ohne Beleg — daraus wurden 4,8 MB HTML,
// 34.568 Elemente und 4.465 Horcher in EINER Seite, das Zehn-
// bis Zwanzigfache jeder anderen. Safari hat den Tab darauf
// weggeraeumt („Diese Web-App wurde neu geladen, weil sie sehr
// viel Speicher verbraucht hat"). Keine Liste im Haus hatte eine
// Obergrenze; die anderen blieben nur klein, weil ihr
// Statusfilter wenig uebrig liess.
//
// WARUM KEIN STILLER ZEITFILTER: der naechstliegende Ausweg
// waere gewesen, die Standardansicht auf das laufende Jahr zu
// stellen. Das versteckt Arbeit — ein Umsatz aus dem Vorjahr
// ohne Beleg ist genau der, den niemand mehr findet. Das
// Sichtfenster zeigt stattdessen die WAHRE Zahl und nur einen
// Teil der Zeilen: „200 von 1.476" steht da, und der Rest ist
// einen Knopf entfernt.
//
// IN DER ADRESSE steht dafuer ein Schluessel: `umfang`. Er
// gehoert zur Ansicht wie der Filter, ist also teilbar und
// haelt dem Neuladen stand. Jede Filteraenderung LOESCHT ihn
// (siehe `useAnsicht` in components/liste/filterkopf.tsx) — wer
// den Filter umstellt, will nicht das Fenster der vorigen
// Auswahl weiterschleppen.
//
// DIE SUMMEN RECHNET DER SERVER WEITER UEBER ALLES, was zum
// Filter passt, nicht ueber das Fenster. Eine Summenzeile, die
// nur die gezeigten Zeilen zaehlt, waere schlimmer als keine.
// ============================================================

/** So viele Zeilen zeigt eine Liste ohne Zutun, und so viele kommen je Klick dazu. */
export const UMFANG_STUFE = 200;

/** Was die Adresse ueber das Sichtfenster sagt. */
export function umfangLesen(p: Record<string, string | string[] | undefined>): number {
  const wert = p.umfang;
  const roh = Number((Array.isArray(wert) ? wert[0] : wert) ?? "");
  if (!Number.isFinite(roh) || roh <= 0) return UMFANG_STUFE;
  // Auf ganze Stufen runden und nach oben begrenzen: aus einer
  // Adresse mit `?umfang=999999` darf kein Weg zurueck in die
  // 4,8-MB-Seite werden.
  const stufen = Math.min(Math.ceil(roh / UMFANG_STUFE), 25);
  return stufen * UMFANG_STUFE;
}

export type Sichtfenster<T> = {
  /** Die Zeilen, die wirklich gezeichnet werden. */
  gezeigt: T[];
  /** Wie viele zum Filter passen — die Zahl, die in der Liste steht. */
  gesamt: number;
  /** Der naechste Umfang, oder `null`, wenn schon alles zu sehen ist. */
  weiter: number | null;
};

/**
 * Den Ausschnitt bestimmen.
 *
 * `gemeint` ist die Zeile, die die Adresse aufschlagen will (ein
 * Verweis von der Rechnung auf ihren Umsatz). Sie kommt IMMER
 * mit, auch wenn sie jenseits der Grenze liegt — sonst fuehrte
 * derselbe Verweis je nach Bestand ins Leere, und das waere ein
 * Fehler, den niemand sucht.
 */
export function sichtfenster<T extends { id: string }>(
  passend: T[],
  umfang: number,
  gemeint?: string,
): Sichtfenster<T> {
  if (passend.length <= umfang) {
    return { gezeigt: passend, gesamt: passend.length, weiter: null };
  }

  const gezeigt = passend.slice(0, umfang);
  if (gemeint && !gezeigt.some((zeile) => zeile.id === gemeint)) {
    const dazu = passend.find((zeile) => zeile.id === gemeint);
    if (dazu) gezeigt.push(dazu);
  }

  return { gezeigt, gesamt: passend.length, weiter: umfang + UMFANG_STUFE };
}

/**
 * Die Adresse fuer „weitere zeigen".
 *
 * Gebaut auf dem Server, weil dort die Suchparameter der Seite
 * ohnehin liegen — ein Client-Bauteil dafuer waere ein
 * Umweg ueber `useSearchParams`, nur um dasselbe zu tun.
 */
export function weiterHref(
  pfad: string,
  p: Record<string, string | string[] | undefined>,
  umfang: number,
): string {
  const suche = new URLSearchParams();
  for (const [name, wert] of Object.entries(p)) {
    if (name === "umfang") continue;
    const eins = Array.isArray(wert) ? wert[0] : wert;
    if (eins) suche.set(name, eins);
  }
  suche.set("umfang", String(umfang));
  return `${pfad}?${suche.toString()}`;
}
